export interface Currency {
  code: string;
  name: string;
  symbol: string;
  symbolPosition: 'before' | 'after';
  decimalPlaces: number;
  thousandSeparator: string;
  decimalSeparator: string;
}

export const CURRENCIES: Currency[] = [
  {
    code: 'USD',
    name: 'US Dollar',
    symbol: '$',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandSeparator: ',',
    decimalSeparator: '.'
  },
  {
    code: 'EUR',
    name: 'Euro',
    symbol: '€',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandSeparator: '.',
    decimalSeparator: ','
  },
  {
    code: 'GBP',
    name: 'British Pound',
    symbol: '£',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandSeparator: ',',
    decimalSeparator: '.'
  },
  {
    code: 'SAR',
    name: 'Saudi Riyal',
    symbol: 'ر.س',
    symbolPosition: 'after',
    decimalPlaces: 2,
    thousandSeparator: ',',
    decimalSeparator: '.'
  },
  {
    code: 'AED',
    name: 'UAE Dirham',
    symbol: 'د.إ',
    symbolPosition: 'after',
    decimalPlaces: 2,
    thousandSeparator: ',',
    decimalSeparator: '.'
  },
  {
    code: 'EGP',
    name: 'Egyptian Pound',
    symbol: 'E£',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandSeparator: ',',
    decimalSeparator: '.'
  },
  {
    code: 'KWD',
    name: 'Kuwaiti Dinar',
    symbol: 'د.ك',
    symbolPosition: 'after',
    decimalPlaces: 3,
    thousandSeparator: ',',
    decimalSeparator: '.'
  },
  {
    code: 'QAR',
    name: 'Qatari Riyal',
    symbol: 'ر.ق',
    symbolPosition: 'after',
    decimalPlaces: 2,
    thousandSeparator: ',',
    decimalSeparator: '.'
  },
  {
    code: 'BHD',
    name: 'Bahraini Dinar',
    symbol: 'د.ب',
    symbolPosition: 'after',
    decimalPlaces: 3,
    thousandSeparator: ',',
    decimalSeparator: '.'
  },
  {
    code: 'OMR',
    name: 'Omani Rial',
    symbol: 'ر.ع',
    symbolPosition: 'after',
    decimalPlaces: 3,
    thousandSeparator: ',',
    decimalSeparator: '.'
  },
  {
    code: 'JPY',
    name: 'Japanese Yen',
    symbol: '¥',
    symbolPosition: 'before',
    decimalPlaces: 0,
    thousandSeparator: ',',
    decimalSeparator: '.'
  },
  {
    code: 'CNY',
    name: 'Chinese Yuan',
    symbol: '¥',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandSeparator: ',',
    decimalSeparator: '.'
  },
  {
    code: 'INR',
    name: 'Indian Rupee',
    symbol: '₹',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandSeparator: ',',
    decimalSeparator: '.'
  },
  {
    code: 'CAD',
    name: 'Canadian Dollar',
    symbol: 'C$',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandSeparator: ',',
    decimalSeparator: '.'
  },
  {
    code: 'AUD',
    name: 'Australian Dollar',
    symbol: 'A$',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandSeparator: ',',
    decimalSeparator: '.'
  },
  {
    code: 'CHF',
    name: 'Swiss Franc',
    symbol: 'CHF',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandSeparator: "'",
    decimalSeparator: '.'
  },
  {
    code: 'SGD',
    name: 'Singapore Dollar',
    symbol: 'S$',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandSeparator: ',',
    decimalSeparator: '.'
  },
  {
    code: 'MYR',
    name: 'Malaysian Ringgit',
    symbol: 'RM',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandSeparator: ',',
    decimalSeparator: '.'
  },
  {
    code: 'PKR',
    name: 'Pakistani Rupee',
    symbol: '₨',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandSeparator: ',',
    decimalSeparator: '.'
  },
  {
    code: 'TRY',
    name: 'Turkish Lira',
    symbol: '₺',
    symbolPosition: 'before',
    decimalPlaces: 2,
    thousandSeparator: '.',
    decimalSeparator: ','
  }
];

export const CURRENCY_MAP = CURRENCIES.reduce((acc, currency) => {
  acc[currency.code] = currency;
  return acc;
}, {} as Record<string, Currency>);

export const DEFAULT_CURRENCY = 'USD';

export function getCurrency(code: string): Currency {
  return CURRENCY_MAP[code] || CURRENCY_MAP[DEFAULT_CURRENCY];
}

export function formatCurrency(amount: number, currencyCode: string): string {
  const currency = getCurrency(currencyCode);
  const absoluteAmount = Math.abs(amount);
  const isNegative = amount < 0;

  const parts = absoluteAmount.toFixed(currency.decimalPlaces).split('.');
  const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, currency.thousandSeparator);
  const decimalPart = parts[1];

  let formattedNumber = decimalPart
    ? `${integerPart}${currency.decimalSeparator}${decimalPart}`
    : integerPart;

  if (isNegative) {
    formattedNumber = `-${formattedNumber}`;
  }

  if (currency.symbolPosition === 'before') {
    return `${currency.symbol}${formattedNumber}`;
  } else {
    return `${formattedNumber} ${currency.symbol}`;
  }
}

export function parseCurrencyInput(value: string, currencyCode: string): number {
  const currency = getCurrency(currencyCode);
  const cleaned = value
    .replace(new RegExp(`\\${currency.thousandSeparator}`, 'g'), '')
    .replace(currency.decimalSeparator, '.')
    .replace(/[^\d.-]/g, '');

  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
}
